import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Card,
  CardContent,
  Button,
  Badge,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import {
  TrendingUp,
  Eye,
  FileText,
  Clock,
  User,
  Calendar,
  Loader2,
  BarChart3,
  AlertCircle,
} from 'lucide-react'
import { jobsApi } from '@/services/api'

interface Report {
  id: string
  customerId: string
  type: string
  status: string
  createdAt: string
  riskScore?: number
  memoId?: string
}

export function Reports() {
  const [reports, setReports] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'completed' | 'pending'>('all')

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const { jobs } = await jobsApi.list({ pageSize: 50 })
        
        const reportData: Report[] = jobs.map((job: any) => ({
          id: job.id,
          customerId: job.customerId,
          type: job.type,
          status: job.status,
          createdAt: job.createdAt,
          riskScore: job.result?.risk_score,
          memoId: job.outputMemoId,
        }))
        
        setReports(reportData)
      } catch (error) {
        console.error('Failed to fetch reports:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchReports()
  }, [])

  const filteredReports = reports.filter(r => {
    if (filter === 'completed') return r.status === 'completed'
    if (filter === 'pending') return r.status === 'pending' || r.status === 'running'
    return true
  })

  const completedCount = reports.filter(r => r.status === 'completed').length
  const pendingCount = reports.filter(r => r.status === 'pending' || r.status === 'running').length
  const avgRiskScore = reports
    .filter(r => r.riskScore !== undefined)
    .reduce((sum, r, _, arr) => sum + (r.riskScore || 0) / arr.length, 0)

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success">Completed</Badge>
      case 'running':
        return <Badge variant="warning">Running</Badge>
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  // Risk level on 10-point scale (1-10, higher = riskier)
  const getRiskLevel = (score?: number) => {
    if (!score) return null
    if (score <= 3) return { label: 'Low', color: 'text-green-600 bg-green-100' }
    if (score <= 6) return { label: 'Medium', color: 'text-yellow-600 bg-yellow-100' }
    return { label: 'High', color: 'text-red-600 bg-red-100' }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-muted-foreground">
            View analysis reports and credit memos
          </p>
        </div>
        <Link to="/upload">
          <Button className="gap-2">
            <FileText className="h-4 w-4" />
            New Report
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-blue-100">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{reports.length}</p>
                <p className="text-sm text-muted-foreground">Total Reports</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-green-100">
                <BarChart3 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{completedCount}</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-yellow-100">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingCount}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-purple-100">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{avgRiskScore.toFixed(1) || 'N/A'}</p>
                <p className="text-sm text-muted-foreground">Avg Risk Score</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <Tabs defaultValue="all" onValueChange={(v) => setFilter(v as typeof filter)}>
        <TabsList>
          <TabsTrigger value="all">All Reports ({reports.length})</TabsTrigger>
          <TabsTrigger value="completed">Completed ({completedCount})</TabsTrigger>
          <TabsTrigger value="pending">Pending ({pendingCount})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Reports Grid - PDF Preview Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : filteredReports.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No reports yet</h3>
              <p className="text-muted-foreground mt-1">
                Run an analysis to generate your first report
              </p>
              <Link to="/upload">
                <Button className="mt-4 gap-2">
                  <FileText className="h-4 w-4" />
                  Upload & Analyze
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-3 gap-6">
          {filteredReports.map((report) => {
            const riskLevel = getRiskLevel(report.riskScore)
            return (
              <Card key={report.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                {/* PDF Preview Header - Mimics document look */}
                <div className="bg-gradient-to-br from-slate-800 to-slate-700 p-4 text-white">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      <span className="text-xs font-medium uppercase tracking-wider opacity-80">
                        Credit Memo
                      </span>
                    </div>
                    {getStatusBadge(report.status)}
                  </div>
                  <h3 className="text-lg font-bold mt-3">{report.customerId}</h3>
                  <p className="text-sm opacity-70 capitalize">{report.type} Analysis</p>
                </div>

                {/* Document Preview Body */}
                <CardContent className="p-4 space-y-3">
                  {/* Risk Score Section */}
                  {riskLevel && report.riskScore ? (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Risk Score</p>
                        <p className="text-2xl font-bold text-gray-900">{Math.round(report.riskScore)}/10</p>
                      </div>
                      <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${riskLevel.color}`}>
                        {riskLevel.label} Risk
                      </span>
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg bg-gray-50 text-center text-gray-400 text-sm">
                      {report.status === 'pending' ? 'Analysis in progress...' : 'No score available'}
                    </div>
                  )}

                  {/* Summary Lines - Mimics document content */}
                  <div className="space-y-2 border-t pt-3">
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">{report.customerId}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">{formatDate(report.createdAt)}</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2 border-t">
                    {report.status === 'completed' ? (
                      <>
                        <Link to={`/analysis/${report.id}`} className="flex-1">
                          <Button variant="outline" size="sm" className="w-full gap-2">
                            <Eye className="h-4 w-4" />
                            View Details
                          </Button>
                        </Link>
                      </>
                    ) : report.status === 'failed' ? (
                      <Button variant="outline" size="sm" className="w-full text-red-600" disabled>
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Analysis Failed
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="w-full" disabled>
                        <Clock className="h-4 w-4 mr-2" />
                        Processing...
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
